import React, { useState, useMemo } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, Chrome, Shield, Loader2, ArrowLeft, KeyRound } from 'lucide-react';
import { toast } from 'react-hot-toast';
import authService from '../firebase/authService';
import { careerAPI } from '../utils/api';
import { CornerBrackets, TechInput, TechButton, StatusIndicator } from './TechUI';

// Password strength calculator
const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 6) score++;
  if (password.length >= 10) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 2) return { score, label: 'WEAK', color: '#ef4444', width: '33%' };
  if (score <= 3) return { score, label: 'MEDIUM', color: '#f59e0b', width: '66%' };
  return { score, label: 'STRONG', color: '#10b981', width: '100%' };
};

const AuthModal = ({ isOpen, onClose, onSuccess, mode: initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      toast.error('Email and password required');
      return false;
    }

    if (mode === 'signup') {
      if (!formData.displayName) {
        toast.error('Display name required');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return false;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return false;
      }
    }

    return true;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      let result;

      if (mode === 'login') {
        result = await authService.signInWithEmail(formData.email, formData.password);

        if (result.error) {
          toast.error(result.error);
          setIsLoading(false);
          return;
        }
      } else {
        // Sign up flow
        result = await authService.signUpWithEmail(
          formData.email,
          formData.password,
          formData.displayName
        );

        if (result.error) {
          toast.error(result.error);
          setIsLoading(false);
          return;
        }
      }

      // If we get here, auth was successful
      // Try to create backend profile, but don't fail if it doesn't work
      try {
        await careerAPI.createUserProfile({
          displayName: result.user.displayName || formData.displayName,
          email: result.user.email,
          photoURL: result.user.photoURL || ''
        });
      } catch (profileError) {
        // This is fine - profile might already exist or will be created on first API call
        console.log('Profile sync note:', profileError.message);
      }

      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created! Check your email to verify.');
      onSuccess && onSuccess(result.user);
      onClose();
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setIsLoading(true);
    try {
      const result = await authService.signInWithGoogle();

      if (result.error) {
        toast.error(result.error);
      } else {
        try {
          await careerAPI.createUserProfile({
            displayName: result.user.displayName,
            email: result.user.email,
            photoURL: result.user.photoURL || ''
          });
        } catch (profileError) {
          console.log('Profile creation handled by backend fallback');
        }

        toast.success('Google authentication successful');
        onSuccess && onSuccess(result.user);
        onClose();
      }
    } catch (error) {
      toast.error(error.message || 'Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', confirmPassword: '', displayName: '' });
    setShowPassword(false);
    setErrors({});
    setIsLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setErrors({ email: 'Email is required' });
      return;
    }
    setIsLoading(true);
    const { error } = await authService.resetPassword(formData.email);
    setIsLoading(false);
    if (error) {
      toast.error(error);
    } else {
      toast.success('Password reset link sent! Check your inbox.');
      setMode('login');
      resetForm();
    }
  };

  const switchMode = () => {
    setMode(prev => prev === 'login' ? 'signup' : 'login');
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-md w-full mx-auto shadow-2xl relative">
        <CornerBrackets />

        {/* Grid Background */}
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px),
                              linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)`,
            backgroundSize: '20px 20px'
          }}
        />

        {/* Header */}
        <div className="relative flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-emerald-500/10 border border-emerald-500/20">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-sm font-mono font-bold text-white uppercase tracking-wider">
                {mode === 'login' ? 'AUTH_LOGIN' : mode === 'signup' ? 'AUTH_REGISTER' : 'RESET_PASSWORD'}
              </h2>
              <StatusIndicator active label={mode === 'login' ? 'SECURE_SESSION' : mode === 'signup' ? 'NEW_USER' : 'RECOVERY_MODE'} />
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/5 transition-colors text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 relative">
          {/* Forgot Password Mode */}
          {mode === 'forgot' ? (
            <div>
              <button
                onClick={() => { setMode('login'); resetForm(); }}
                className="flex items-center gap-2 text-xs font-mono text-slate-500 hover:text-emerald-400 transition-colors mb-6"
              >
                <ArrowLeft className="w-3 h-3" />
                BACK_TO_LOGIN
              </button>

              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <KeyRound className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-white font-medium">Reset your password</p>
                  <p className="text-xs text-slate-500 font-mono">We'll send a reset link to your email</p>
                </div>
              </div>

              <form onSubmit={handleForgotPassword} className="space-y-5">
                <TechInput
                  label="Email_Address"
                  icon={Mail}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="user@domain.com"
                  disabled={isLoading}
                />

                <TechButton
                  type="submit"
                  variant="primary"
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="ml-2">SENDING...</span>
                    </>
                  ) : (
                    '[ SEND_RESET_LINK ]'
                  )}
                </TechButton>
              </form>
            </div>
          ) : (
            <>
              {/* Google Sign In */}
              <button
                onClick={handleGoogleAuth}
                disabled={isLoading}
                className="w-full flex items-center justify-center space-x-3 bg-white text-gray-900 py-4 px-4 font-mono text-xs font-bold hover:bg-gray-100 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-white/20"
              >
                <Chrome className="w-4 h-4" />
                <span>GOOGLE_AUTH</span>
              </button>

              <div className="flex items-center my-6">
                <div className="flex-1 h-px bg-white/10"></div>
                <span className="px-4 text-slate-500 text-[10px] font-mono uppercase tracking-widest">Or Manual</span>
                <div className="flex-1 h-px bg-white/10"></div>
              </div>

              {/* Email/Password Form */}
              <form onSubmit={handleEmailAuth} className="space-y-5">
                {mode === 'signup' && (
                  <TechInput
                    label="User_ID"
                    icon={User}
                    type="text"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    placeholder="ENTER_NAME"
                    disabled={isLoading}
                  />
                )}

                <TechInput
                  label="Email_Address"
                  icon={Mail}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="user@domain.com"
                  disabled={isLoading}
                />

                <div className="relative">
                  <TechInput
                    label="Password"
                    icon={Lock}
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-[38px] text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>

                  {/* Password Strength Meter */}
                  {mode === 'signup' && formData.password && (
                    <div className="mt-2">
                      <div className="strength-meter">
                        <div
                          className="strength-meter-fill"
                          style={{
                            width: getPasswordStrength(formData.password).width,
                            background: getPasswordStrength(formData.password).color
                          }}
                        />
                      </div>
                      <span className="text-[10px] font-mono uppercase tracking-wider mt-1 block" style={{ color: getPasswordStrength(formData.password).color }}>
                        {getPasswordStrength(formData.password).label}
                      </span>
                    </div>
                  )}
                </div>

                {mode === 'signup' && (
                  <TechInput
                    label="Confirm_Password"
                    icon={Lock}
                    type={showPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                )}

                {mode === 'login' && (
                  <div className="text-right -mt-2">
                    <button
                      type="button"
                      onClick={() => { setMode('forgot'); resetForm(); }}
                      className="text-xs font-mono text-slate-500 hover:text-emerald-400 transition-colors"
                    >
                      FORGOT_PASSWORD?
                    </button>
                  </div>
                )}

                <TechButton
                  type="submit"
                  variant="primary"
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="ml-2">PROCESSING...</span>
                    </>
                  ) : (
                    mode === 'login' ? '[ LOGIN ]' : '[ REGISTER ]'
                  )}
                </TechButton>
              </form>

              {/* Switch Mode */}
              <div className="mt-6 text-center">
                <p className="text-slate-500 text-xs font-mono">
                  {mode === 'login' ? 'NO_ACCOUNT?' : 'HAVE_ACCOUNT?'}
                  {' '}
                  <button
                    onClick={switchMode}
                    className="text-emerald-400 hover:text-emerald-300 font-bold transition-colors"
                    disabled={isLoading}
                  >
                    {mode === 'login' ? 'REGISTER' : 'LOGIN'}
                  </button>
                </p>
              </div>

              {/* Footer */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <p className="text-[10px] text-slate-600 text-center font-mono uppercase tracking-wider mb-2">
                  Encrypted Connection • 256-bit TLS
                </p>
                <p className="text-[9px] text-slate-600 text-center font-mono">
                  By continuing, you agree to our{' '}
                  <a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors underline">Terms</a>{' & '}
                  <a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors underline">Privacy</a>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
